import React from "react";
import db from "../../../services/query";
import AriaReport, {
  attendanceMark,
  buildPrintDocumentTitle,
  faDigits,
  ReportSignatures,
  usePrintData,
  weekdayFa,
} from "./AriaReport";

export default function WeeklyAttendancePrint({ match }) {
  const { classId, startDate } = match.params;
  const { loading, error, data, summary } = usePrintData(
    async () => {
      const { data: body } = await db.getAll(
        `mis-parity/reports/weekly-attendance?classId=${classId}&startDate=${startDate}`
      );
      return body;
    },
    [classId, startDate]
  );
  const students = Array.isArray(data) ? data : [];
  const cls = summary?.cls || {};
  const lectures = summary?.lectures || [];
  const daily = summary?.daily || [];
  const groups = daily.map((entry, dayIndex) => {
    const weekday = Number(entry.day?.weekday_no);
    const dayLectures = lectures.filter((lecture) => Number(lecture.weekday) === weekday);
    return {
      day: entry.day || { weekday_no: dayIndex + 1 },
      lectures: dayLectures.length
        ? dayLectures
        : [{ lecture_id: `blank-${dayIndex}`, subject_name: "" }],
      byStudent: (entry.data || []).reduce((map, row) => {
        const key = Number(row.student_id);
        if (!map[key]) map[key] = {};
        map[key][Number(row.lecture_id)] = row.attendance_state;
        return map;
      }, {}),
    };
  });
  const firstDate = daily[0]?.day?.the_date;
  const lastDate = daily[daily.length - 1]?.day?.the_date;
  const dateDescription = faDigits(
    daily.find((entry) => entry.day?.date_description)?.day?.date_description
    || [firstDate, lastDate].filter(Boolean).join(" - ")
  );

  if (loading) return <p className="aria-report-status">در حال آماده‌سازی گزارش…</p>;
  if (error) return <p className="aria-report-status" role="alert">{error}</p>;

  return (
    <AriaReport
      className="weekly-attendance-report"
      landscape
      pageMargin="8mm"
      documentTitle={buildPrintDocumentTitle(["حاضری هفتگی", cls.class_desc, dateDescription])}
      rightLines={["گزارش حاضری محصلین", cls.class_desc, dateDescription]}
      leftLines={["وزارت تحصیلات عالی", "پوهنتون آریا", cls.faculty_name, "مدیریت تدریسی"]}
    >
      <table className="aria-report-table weekly-attendance-table">
        <thead>
          <tr>
            <th rowSpan={3} className="serial-col">شماره</th>
            <th colSpan={3} rowSpan={2}>شهرت</th>
            {groups.map((group) => (
              <th
                key={`weekday-${group.day.weekday_no}`}
                colSpan={group.lectures.length}
                className="day-group"
              >
                {weekdayFa(group.day.weekday_name_fa)}
              </th>
            ))}
            <th colSpan={2} rowSpan={2}>مجموعه</th>
            <th rowSpan={3} className="notes-col">ملاحظات</th>
          </tr>
          <tr>
            {groups.map((group) => (
              <th
                key={`date-${group.day.weekday_no}`}
                colSpan={group.lectures.length}
                className="day-date"
              >
                {faDigits(group.day.the_date || "")}
              </th>
            ))}
          </tr>
          <tr>
            <th className="name-col">نام</th>
            <th className="name-col">نام پدر</th>
            <th className="student-code-col">کد دانشجویی</th>
            {groups.flatMap((group) =>
              group.lectures.map((lecture) => (
                <th key={`${group.day.the_date}-${lecture.lecture_id}`} className="subject-name">
                  <span className="vlabel">{lecture.subject_name}</span>
                </th>
              ))
            )}
            <th className="total-col">غیر حاضر</th>
            <th className="total-col">حاضر</th>
          </tr>
        </thead>
        <tbody>
          {students.length === 0 ? (
            <tr>
              <td colSpan={7 + groups.reduce((sum, group) => sum + group.lectures.length, 0)}>
                <div className="aria-report-empty">محصلی برای این صنف ثبت نشده است</div>
              </td>
            </tr>
          ) : students.map((row, index) => {
            const present = Number(row.present_count || 0);
            const total = Number(row.total_count || 0);
            const absent = Math.max(0, total - present);
            return (
              <tr key={row.student_id || index}>
                <td>{faDigits(index + 1)}</td>
                <td className="name-col">{row.student_name}</td>
                <td className="name-col">{row.student_father_name}</td>
                <td className="student-code-col">{row.aria_id || row.tawana_id || ""}</td>
                {groups.flatMap((group) =>
                  group.lectures.map((lecture) => (
                    <td
                      key={`${row.student_id}-${group.day.the_date}-${lecture.lecture_id}`}
                      className="mark-col"
                    >
                      {attendanceMark(group.byStudent[Number(row.student_id)]?.[Number(lecture.lecture_id)])}
                    </td>
                  ))
                )}
                <td className="total-col">{faDigits(absent)}</td>
                <td className="total-col">{faDigits(present)}</td>
                <td className="notes-col" />
              </tr>
            );
          })}
        </tbody>
      </table>
      <ReportSignatures labels={["مدیر تدریسی", "ریس پوهنحی"]} />
    </AriaReport>
  );
}
